import { useRef } from "react";
import QRCode from "react-qr-code";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { API, LOGO_URL, DEFAULT_AVATAR } from "@/lib/api";
import { Button } from "@/components/ui/button";

export const MemberIdCard = ({ member }) => {
  const cardRef = useRef(null);
  const isOfficial = member.role_type === "official" && member.position;
  const roleLabel = isOfficial ? member.position : "MEMBER";
  const photoSrc = member.photo ? `${API}/files/${member.photo}` : DEFAULT_AVATAR;

  const download = async () => {
    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 3,
        useCORS: true,
        backgroundColor: null,
        onclone: (doc) => {
          const card = doc.querySelector('[data-testid="member-id-card"]');
          if (card) {
            card.querySelectorAll("span, div, p").forEach((n) => {
              n.style.lineHeight = "1.6";
              n.style.overflow = "visible";
              n.style.whiteSpace = "normal";
            });
          }
        },
      });
      const link = document.createElement("a");
      link.download = `${member.member_id}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) {
      toast.error("Could not generate the card image. Please try again.");
    }
  };

  return (
    <div className="flex flex-col items-center gap-5">
      <div
        ref={cardRef}
        data-testid="member-id-card"
        className="relative w-full max-w-sm rounded-2xl overflow-hidden text-white shadow-2xl"
        style={{ background: "linear-gradient(135deg, #008751 0%, #024d30 100%)" }}
      >
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-secondary/20 blur-2xl pointer-events-none" />
        <div className="absolute -right-12 -bottom-12 w-32 h-32 opacity-[0.05] pointer-events-none">
          <img src={LOGO_URL} alt="" crossOrigin="anonymous" className="w-full h-full object-contain" />
        </div>

        <div className="relative z-10 p-5 pb-6 flex flex-col gap-4">
          {/* header */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <img src={LOGO_URL} alt="LBM" crossOrigin="anonymous" className="w-9 h-9 rounded-full object-contain bg-white p-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-heading font-extrabold text-sm leading-snug">LONDON BOY</div>
                <div className="text-[9px] tracking-[0.18em] text-secondary font-bold uppercase leading-snug pb-0.5">MOVEMENT · {roleLabel}</div>
              </div>
            </div>
            <div className="bg-white p-1.5 rounded-lg shrink-0">
              <QRCode value={member.member_id} size={50} />
            </div>
          </div>

          {/* identity */}
          <div className="flex items-center gap-3">
            <img
              src={photoSrc}
              alt="member"
              crossOrigin="anonymous"
              className="w-16 h-16 rounded-xl object-cover ring-2 ring-white/40 bg-white shrink-0"
            />
            <div className="min-w-0 flex-1">
              <div className="font-heading font-extrabold text-base leading-snug break-words pb-0.5">{member.full_name}</div>
              <div className="text-[11px] text-emerald-100 font-mono leading-snug pb-0.5">{member.member_id}</div>
              <span className={`inline-block mt-1 text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded-full leading-none ${isOfficial ? "bg-secondary text-slate-900" : "bg-white/15 text-white"}`}>
                {isOfficial ? member.position : "Registered Member"}
              </span>
            </div>
          </div>

          {/* footer */}
          <div className="grid grid-cols-3 gap-2 text-[10px] border-t border-white/20 pt-3">
            <div>
              <div className="text-emerald-200/70 uppercase leading-snug pb-0.5">LGA</div>
              <div className="font-semibold leading-snug break-words">{member.lga}</div>
            </div>
            <div>
              <div className="text-emerald-200/70 uppercase leading-snug pb-0.5">Ward</div>
              <div className="font-semibold leading-snug break-words">{member.ward}</div>
            </div>
            <div>
              <div className="text-emerald-200/70 uppercase leading-snug pb-0.5">Joined</div>
              <div className="font-semibold leading-snug">{(member.date_joined || "").slice(0, 10)}</div>
            </div>
          </div>
        </div>
      </div>

      <Button onClick={download} data-testid="download-id-card-btn" className="rounded-full bg-primary hover:bg-primary/90 font-bold gap-2 px-6">
        <Download className="w-4 h-4" /> Download ID Card
      </Button>
    </div>
  );
};
